'use client';

import { normalizeCmsPopupId } from '@/lib/utils/normalizeCmsPopupId';
import type { FC, PropsWithChildren, ReactNode } from 'react';
import { useLayoutEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

import type { PopupAnimation, PopupMaxWidth } from './Popup.types';

export interface PopupEntry {
  popupId: string;
  animation?: PopupAnimation | null;
  maxWidth?: PopupMaxWidth | null;
  backgroundColor?: string | null;
  children: ReactNode;
}

const entries = new Map<string, PopupEntry>();
let registryVersion = 0;
const listeners = new Set<() => void>();
let lastKnownPathname = '';

const notifyRegistrySubscribers = (): void => {
  registryVersion += 1;
  listeners.forEach((listener) => listener());
};

export const popupEntries = {
  /**
   * Called by PopupRegistry before registering. When the pathname changes
   * (client-side navigation), all entries from the previous page are cleared.
   * Because PopupRegistry fires in useLayoutEffect and React commits siblings
   * in render order, the first PopupRegistry on the new page clears stale
   * entries before any new ones are added.
   */
  notifyRouteChange: (pathname: string) => {
    if (lastKnownPathname && lastKnownPathname !== pathname) {
      entries.clear();
      notifyRegistrySubscribers();
    }
    lastKnownPathname = pathname;
  },
  register: (entry: PopupEntry) => {
    const id = normalizeCmsPopupId(entry.popupId);
    if (!id) {
      return;
    }
    entries.set(id, { ...entry, popupId: id });
    notifyRegistrySubscribers();
  },
  unregister: (popupId: string) => {
    const id = normalizeCmsPopupId(popupId);
    entries.delete(id);
    notifyRegistrySubscribers();
  },
  getEntry: (popupId: string) => {
    return entries.get(normalizeCmsPopupId(popupId));
  },
  clear: () => {
    entries.clear();
    lastKnownPathname = '';
    notifyRegistrySubscribers();
  },
  subscribe: (onStoreChange: () => void): (() => void) => {
    listeners.add(onStoreChange);
    return () => listeners.delete(onStoreChange);
  },
  getRegistryVersion: (): number => registryVersion,
};

interface PopupRegistryProps {
  popupId: string;
  animation?: PopupAnimation | null;
  maxWidth?: PopupMaxWidth | null;
  backgroundColor?: string | null;
}

/**
 * Registers popup content in the module map (renders nothing).
 * Children updates use a ref so deps stay stable.
 *
 * Does not unregister on unmount — nested popups (popup A's content contains
 * popup B's PopupRegistry) must survive when switching targets. Instead,
 * the entire registry is cleared on client-side navigation via
 * `notifyRouteChange`, so stale entries from previous pages don't accumulate.
 */
export const PopupRegistry: FC<PropsWithChildren<PopupRegistryProps>> = ({
  popupId,
  animation,
  maxWidth,
  backgroundColor,
  children,
}) => {
  const pathname = usePathname();
  const childrenRef = useRef(children);
  childrenRef.current = children;

  useLayoutEffect(() => {
    popupEntries.notifyRouteChange(pathname);
    popupEntries.register({
      popupId,
      animation,
      maxWidth,
      backgroundColor,
      children: childrenRef.current,
    });
  }, [popupId, animation, maxWidth, backgroundColor, pathname]);

  return null;
};
